import { useMemo, type ReactNode } from 'react';
import { ProposedApiContext, ProposedEnabledContext } from './context';
import { MockProposedApi } from './mock';

export interface ProposedApiProviderProps {
  /**
   * Whether Wave-4 prototype surfaces are allowed to render. Production
   * (real-API) builds pass `false`: the proposed API is an in-memory mock and
   * must never masquerade as live data (ADR-0013). Mock-mode builds
   * (`VITE_USE_MOCK=1`) pass `true` to demo the surfaces.
   */
  enabled: boolean;
  children: ReactNode;
}

/**
 * Provides the proposed Wave-4 API. Currently always the in-repo mock; a real
 * implementation replaces `MockProposedApi` when the backend pins the contracts.
 * The provider itself is always mounted so consumer hooks are stable; `enabled`
 * decides whether any prototype UI renders.
 */
export function ProposedApiProvider({
  enabled,
  children,
}: ProposedApiProviderProps): JSX.Element {
  const api = useMemo(() => new MockProposedApi(), []);
  return (
    <ProposedEnabledContext.Provider value={enabled}>
      <ProposedApiContext.Provider value={api}>{children}</ProposedApiContext.Provider>
    </ProposedEnabledContext.Provider>
  );
}
